import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useTheme } from "../../components/theme-provider";
import {
  Search,
  Eye,
  XCircle,
  BookOpen,
  Calendar,
  User,
  Loader2,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { format } from "date-fns";
import { getDateFnsLocale } from "../../utils/dateLocale";
import {
  PieChart,
  Pie,
  Cell,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { getStudentGrades } from "../../features/grades/gradeSlice";
import { Button } from "../../components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "../../components/ui/tooltip";
import { TooltipProvider } from "../../components/ui/tooltip";
import { useTranslation } from "react-i18next";
import { Spinner } from "../../components/ui/spinner";
import { Skeleton } from "../../components/ui/skeleton";

const GradesSkeleton = ({ isMobile, t }) => (
  <div className="space-y-6">
    {/* Stats Cards and Grade Distribution Chart Skeleton */}
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      {/* Stats Cards Skeleton */}
      <div className="grid grid-cols-2 gap-4">
        {[1, 2, 3, 4].map((i) => (
          <Card key={i}>
            <CardContent className="p-4 text-center flex flex-col items-center justify-center h-full">
              <Skeleton className="h-4 w-20 mx-auto mb-1" />
              <Skeleton className="h-8 w-16 mx-auto" />
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Grade Distribution Chart Skeleton - Static to reduce movement */}
      <div className="lg:col-span-2">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <div className="h-5 w-5 rounded-full bg-muted/60" />
              <div className="h-6 w-48 bg-muted/40 rounded-md" />
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <div className="h-80 flex justify-center items-center">
              <div className="h-64 w-64 rounded-full bg-muted/20 border-8 border-muted/30" />
            </div>
          </CardContent>
        </Card>
      </div>
    </div>

    {/* Search Input */}
    <div className="flex flex-col sm:flex-row gap-4">
      <div className="relative flex-1">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder={t ? t("student.searchGrades") : "Search grades..."}
          className="pl-10"
          disabled
        />
      </div>
    </div>

    {/* Grades List Skeleton - Static placeholders for stability */}
    <div className="mb-6">
      <div className="h-7 w-32 bg-muted rounded-md mb-4" />
      {isMobile ? (
        <div className="px-1 sm:px-2">
          {[1, 2, 3].map((i) => (
            <Card key={i} className="mb-4 dark:border-gray-600">
              <CardContent className="p-3 sm:p-6">
                <div className="flex justify-between mb-4">
                  <div className="h-6 w-32 bg-muted/50 rounded-md" />
                  <div className="h-6 w-12 bg-muted/40 rounded-lg" />
                </div>
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <div className="h-4 w-4 rounded-full bg-muted/30" />
                    <div className="h-4 w-32 bg-muted/30 rounded-md" />
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="h-4 w-4 rounded-full bg-muted/30" />
                    <div className="h-4 w-40 bg-muted/30 rounded-md" />
                  </div>
                  <div className="h-10 w-full bg-muted/20 rounded-md mt-2" />
                </div>
                <div className="flex justify-end mt-4">
                  <div className="h-9 w-28 bg-muted/40 rounded-md" />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <div className="rounded-lg border bg-card dark:border-gray-600 overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="border-b border-gray-200 dark:border-gray-600">
                <TableHead className="p-4">
                  <div className="h-4 w-24 bg-muted/50 rounded-md" />
                </TableHead>
                <TableHead className="p-4">
                  <div className="h-4 w-12 bg-muted/40 rounded-md mx-auto" />
                </TableHead>
                <TableHead className="p-4">
                  <div className="h-4 w-32 bg-muted/50 rounded-md" />
                </TableHead>
                <TableHead className="p-4">
                  <div className="h-4 w-24 bg-muted/40 rounded-md" />
                </TableHead>
                <TableHead className="p-4">
                  <div className="h-4 w-24 bg-muted/40 rounded-md" />
                </TableHead>
                <TableHead className="p-4 text-center">
                  <div className="h-4 w-16 bg-muted/50 rounded-md mx-auto" />
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {[1, 2, 3, 4, 5].map((i) => (
                <TableRow
                  key={i}
                  className="border-b border-gray-200 dark:border-gray-600"
                >
                  <TableCell className="p-4">
                    <div className="h-5 w-32 bg-muted/30 rounded-md" />
                  </TableCell>
                  <TableCell className="p-4 text-center">
                    <div className="h-6 w-10 bg-muted/40 rounded-lg mx-auto" />
                  </TableCell>
                  <TableCell className="p-4">
                    <div className="h-5 w-48 bg-muted/30 rounded-md" />
                  </TableCell>
                  <TableCell className="p-4">
                    <div className="h-5 w-32 bg-muted/30 rounded-md" />
                  </TableCell>
                  <TableCell className="p-4">
                    <div className="h-5 w-24 bg-muted/30 rounded-md" />
                  </TableCell>
                  <TableCell className="p-4 text-center">
                    <div className="h-9 w-12 bg-muted/40 rounded-md mx-auto" />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  </div>
);

// Import mobile detection and utilities
import { useIsMobile } from "../../components/hooks/use-mobile";
import { cn } from "../../lib/utils";

const StudentGrades = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { darkMode } = useTheme();
  const isMobile = useIsMobile();

  const { user } = useSelector((state) => state.auth);
  const { grades, isLoading } = useSelector((state) => state.grades);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredGrades, setFilteredGrades] = useState([]);
  const [displayedGrades, setDisplayedGrades] = useState([]);
  const [gradeStats, setGradeStats] = useState({
    average: 0,
    highestGrade: 0,
    lowestGrade: 0,
    passingRate: 0,
    gradeDistribution: {},
  });

  useEffect(() => {
    dispatch(getStudentGrades(user._id));
  }, [dispatch, user._id]);

  useEffect(() => {
    if (grades && grades.length > 0) {
      applyFilters();
      calculateStats();
    }
  }, [grades, searchTerm]);

  const applyFilters = () => {
    if (!grades) return;

    let filtered = [...grades];

    // Apply search filter
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (grade) =>
          (grade.subject &&
            grade.subject.name &&
            grade.subject.name.toLowerCase().includes(search)) ||
          (grade.description &&
            grade.description.toLowerCase().includes(search)) ||
          (grade.teacher &&
            grade.teacher.name &&
            grade.teacher.name.toLowerCase().includes(search)),
      );
    }

    setFilteredGrades(filtered);
    setDisplayedGrades(filtered);
  };

  const calculateStats = () => {
    if (!grades || grades.length === 0) return;

    // Calculate average grade
    const sum = grades.reduce((acc, grade) => acc + grade.value, 0);
    const average = sum / grades.length;

    // Calculate highest and lowest grades
    const highestGrade = Math.max(...grades.map((grade) => grade.value));
    const lowestGrade = Math.min(...grades.map((grade) => grade.value));

    // Calculate passing rate (grades >= 50)
    const passingGrades = grades.filter((grade) => grade.value >= 50).length;
    const passingRate = (passingGrades / grades.length) * 100;

    // Calculate grade distribution
    const distribution = {
      "A (90-100)": grades.filter((grade) => grade.value >= 90).length,
      "B (80-89)": grades.filter(
        (grade) => grade.value >= 80 && grade.value < 90,
      ).length,
      "C (70-79)": grades.filter(
        (grade) => grade.value >= 70 && grade.value < 80,
      ).length,
      "D (60-69)": grades.filter(
        (grade) => grade.value >= 60 && grade.value < 70,
      ).length,
      "E (50-59)": grades.filter(
        (grade) => grade.value >= 50 && grade.value < 60,
      ).length,
      "F (0-49)": grades.filter((grade) => grade.value < 50).length,
    };

    setGradeStats({
      average: average.toFixed(2),
      highestGrade,
      lowestGrade,
      passingRate: passingRate.toFixed(2),
      gradeDistribution: distribution,
    });
  };

  const handleChangePage = (newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (newRowsPerPage) => {
    setRowsPerPage(parseInt(newRowsPerPage, 10));
    setPage(0);
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
    setPage(0);
  };

  const handleViewGrade = (id) => {
    navigate(`/app/grades/${id}`);
  };

  // Prepare data for Pie chart
  const pieData = Object.entries(gradeStats.gradeDistribution || {}).map(
    ([name, value]) => ({
      name,
      value,
    }),
  );
  const COLORS = [
    "hsl(175, 50%, 50%)",
    "hsl(205, 80%, 55%)",
    "hsl(260, 100%, 70%)",
    "hsl(45, 100%, 65%)",
    "hsl(25, 100%, 60%)",
    "hsl(350, 100%, 70%)",
  ];

  const getGradeColor = (grade) => {
    if (grade >= 80)
      return "text-white font-semibold" + " " + "bg-[hsl(var(--success))]";
    if (grade >= 60)
      return "text-white font-semibold" + " " + "bg-[hsl(var(--info))]";
    if (grade >= 50)
      return "text-white font-semibold" + " " + "bg-[hsl(var(--warning))]";
    return "text-white font-semibold" + " " + "bg-[hsl(var(--error))]";
  };

  // Mobile card layout for grades
  const renderMobileContent = () => {
    const paginatedGrades = filteredGrades.slice(
      page * rowsPerPage,
      page * rowsPerPage + rowsPerPage,
    );

    if (paginatedGrades.length === 0) {
      return (
        <div className="text-center py-8">
          <BookOpen className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-foreground mb-2">
            {t("student.noGradesAvailable")}
          </h3>
          <p className="text-muted-foreground">
            {!grades || grades.length === 0
              ? t("student.noGradesYet")
              : t("student.noGradesMatch")}
          </p>
        </div>
      );
    }

    return (
      <div className="px-1 sm:px-2">
        {paginatedGrades.map((grade) => (
          <Card
            key={grade._id}
            className="mb-4 hover:shadow-lg hover:-translate-y-0.5 transition-all duration-200 dark:border-gray-600 dark:hover:shadow-gray-800/50"
          >
            <CardContent className="p-3 sm:p-6">
              <div className="flex items-start gap-3">
                <div className="flex-grow min-w-0">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-bold text-lg overflow-hidden text-ellipsis whitespace-nowrap text-foreground">
                      {grade.subject?.name || "N/A"}
                    </h3>
                    <Badge className={getGradeColor(grade.value) + " text-sm"}>
                      {grade.value}
                    </Badge>
                  </div>

                  {grade.teacher?.name && (
                    <div className="flex items-center gap-2 mb-2">
                      <User className="h-4 w-4 text-muted-foreground" />
                      <span className="text-sm text-muted-foreground">
                        {grade.teacher.name}
                      </span>
                    </div>
                  )}

                  <div className="flex items-center gap-2 mb-2">
                    <Calendar className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm text-muted-foreground">
                      {grade.date ? format(new Date(grade.date), "PPP", { locale: getDateFnsLocale() }) : "N/A"}
                    </span>
                  </div>

                  {grade.description && (
                    <div className="mb-2">
                      <p className="text-sm text-muted-foreground line-clamp-2">
                        {grade.description}
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Action button */}
              <div className="flex justify-end mt-4">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleViewGrade(grade._id)}
                  title={t("student.viewDetails")}
                  className="transition-[transform,box-shadow] hover:scale-105 hover:shadow-md hover:border-primary hover:bg-primary/5 hover:text-primary gap-2"
                >
                  <Eye className="h-4 w-4" />
                  {t("student.viewDetails")}
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  };

  // Desktop table layout
  const renderDesktopContent = () => {
    return (
      <div className="rounded-lg border bg-card dark:border-gray-600">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="border-b border-gray-200 dark:border-gray-600">
                <TableHead className="text-left p-4 text-foreground font-medium">
                  {t("student.subject")}
                </TableHead>
                <TableHead className="text-center p-4 text-foreground font-medium">
                  {t("student.grade")}
                </TableHead>
                <TableHead className="text-left p-4 text-foreground font-medium">
                  {t("student.description")}
                </TableHead>
                <TableHead className="text-left p-4 text-foreground font-medium">
                  {t("student.teacher")}
                </TableHead>
                <TableHead className="text-left p-4 text-foreground font-medium">
                  {t("student.date")}
                </TableHead>
                <TableHead className="text-center p-4 text-foreground font-medium">
                  {t("student.actions")}
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredGrades.slice(
                page * rowsPerPage,
                page * rowsPerPage + rowsPerPage,
              ).length > 0 ? (
                filteredGrades
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((grade) => (
                    <TableRow
                      key={grade._id}
                      className="border-b border-gray-200 dark:border-gray-600 hover:bg-primary/5 dark:hover:bg-primary/10 transition-colors duration-300"
                    >
                      <TableCell className="p-4 font-medium text-foreground text-base">
                        {grade.subject?.name || "N/A"}
                      </TableCell>
                      <TableCell className="p-4 text-center">
                        <Badge className={getGradeColor(grade.value)}>
                          {grade.value}
                        </Badge>
                      </TableCell>
                      <TableCell className="p-4 text-foreground text-base max-w-xs truncate">
                        {grade.description || "N/A"}
                      </TableCell>
                      <TableCell className="p-4 text-foreground text-base">
                        {grade.teacher?.name || "N/A"}
                      </TableCell>
                      <TableCell className="p-4 text-foreground text-base">
                        {grade.date
                          ? format(new Date(grade.date), "PPP", { locale: getDateFnsLocale() })
                          : "N/A"}
                      </TableCell>
                      <TableCell className="p-4 text-center">
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleViewGrade(grade._id)}
                              title={t("student.viewDetails")}
                              className="transition-[transform,box-shadow] hover:scale-105 hover:shadow-md hover:border-primary hover:bg-primary/5 hover:text-primary px-4 py-2"
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>{t("student.viewDetails")}</p>
                          </TooltipContent>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
              ) : (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-12">
                    <div className="text-center">
                      <BookOpen className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
                      <h3 className="text-lg font-semibold text-foreground mb-2">
                        {t("student.noGradesAvailable")}
                      </h3>
                      <p className="text-muted-foreground">
                        {!grades || grades.length === 0
                          ? t("student.noGradesYet")
                          : t("student.noGradesMatch")}
                      </p>
                    </div>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </div>
    );
  };

  return (
    <TooltipProvider>
      <div className="container mx-auto p-4 space-y-6">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-3xl font-light tracking-wide text-foreground mb-2">
              {t("student.myGrades")}
            </h1>
            <p className="text-muted-foreground mt-2">
              {t("student.viewAndTrack")}
            </p>
          </div>
        </div>

        {/* Show skeleton only on cold start (no cached grades yet). If Redux already
            has grades from a prior visit or dashboard pre-fetch, render immediately
            and let the background revalidation update silently. */}
        {isLoading && (!grades || grades.length === 0) ? (
          <GradesSkeleton isMobile={isMobile} t={t} />
        ) : grades && grades.length > 0 ? (
          <>
            {/* Stats Cards and Grade Distribution Chart - Side by Side */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Stats Cards */}
              <div className="grid grid-cols-2 gap-4">
                {/* Average Grade */}
                <Card>
                  <CardContent className="p-4 text-center flex flex-col items-center justify-center h-full">
                    <p className="text-sm text-muted-foreground mb-1">
                      {t("student.average")}
                    </p>
                    <p className="text-2xl font-bold text-primary">
                      {gradeStats.average}
                    </p>
                  </CardContent>
                </Card>

                {/* Highest Grade */}
                <Card>
                  <CardContent className="p-4 text-center flex flex-col items-center justify-center h-full">
                    <p className="text-sm text-muted-foreground mb-1">
                      {t("student.highest")}
                    </p>
                    <p
                      className="text-2xl font-bold"
                      style={{ color: "hsl(var(--success))" }}
                    >
                      {gradeStats.highestGrade}
                    </p>
                  </CardContent>
                </Card>

                {/* Lowest Grade */}
                <Card>
                  <CardContent className="p-4 text-center flex flex-col items-center justify-center h-full">
                    <p className="text-sm text-muted-foreground mb-1">
                      {t("student.lowest")}
                    </p>
                    <p
                      className="text-2xl font-bold"
                      style={{ color: "hsl(var(--error))" }}
                    >
                      {gradeStats.lowestGrade}
                    </p>
                  </CardContent>
                </Card>

                {/* Passing Rate */}
                <Card>
                  <CardContent className="p-4 text-center flex flex-col items-center justify-center h-full">
                    <p className="text-sm text-muted-foreground mb-1">
                      {t("student.passingRate")}
                    </p>
                    <p
                      className="text-2xl font-bold"
                      style={{ color: "hsl(var(--info))" }}
                    >
                      {gradeStats.passingRate}%
                    </p>
                  </CardContent>
                </Card>
              </div>

              {/* Grade Distribution Chart */}
              <div className="lg:col-span-2">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <BookOpen className="h-5 w-5" />
                      {t("student.gradeDistribution")}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-6">
                    <div className="h-80 flex justify-center items-center relative">
                      {isLoading ? (
                        <div className="flex flex-col items-center justify-center text-muted-foreground">
                          <Loader2 className="h-8 w-8 animate-spin mb-2" />
                          <p className="text-sm">{t("student.loadingChart")}</p>
                        </div>
                      ) : !grades || grades.length === 0 ? (
                        <div className="flex flex-col items-center justify-center text-muted-foreground">
                          <BookOpen className="h-12 w-12 mb-2 opacity-50" />
                          <p className="text-sm">
                            {t("student.noGradesForChart")}
                          </p>
                        </div>
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <ResponsiveContainer width="100%" height="100%">
                            <PieChart>
                              <Pie
                                data={pieData}
                                cx="50%"
                                cy="50%"
                                labelLine={false}
                                outerRadius={120}
                                fill="#8884d8"
                                dataKey="value"
                                startAngle={90}
                                endAngle={-270}
                              >
                                {pieData.map((entry, index) => (
                                  <Cell
                                    key={`cell-${index}`}
                                    fill={COLORS[index % COLORS.length]}
                                  />
                                ))}
                              </Pie>
                              <RechartsTooltip />
                              <Legend verticalAlign="bottom" height={36} />
                            </PieChart>
                          </ResponsiveContainer>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>

            {/* Search and Filters */}
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder={t("student.searchGrades")}
                  value={searchTerm}
                  onChange={handleSearchChange}
                  className="pl-10"
                />
              </div>
            </div>

            {/* Grades List */}
            <div className="mb-6">
              <h2 className="text-xl font-semibold text-foreground mb-4">
                {t("student.grades")}
              </h2>

              {/* Responsive layout */}
              {isMobile ? renderMobileContent() : renderDesktopContent()}

              {/* Pagination */}
              {filteredGrades.length > 0 && (
                <div className="flex items-center justify-between space-x-2 py-4 mt-4">
                  <div className="flex items-center space-x-2">
                    <p className="text-sm text-muted-foreground">
                      {t("student.rowsPerPage")}
                    </p>
                    <Select
                      value={rowsPerPage.toString()}
                      onValueChange={handleChangeRowsPerPage}
                    >
                      <SelectTrigger className="h-8 w-[70px]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="5">5</SelectItem>
                        <SelectItem value="10">10</SelectItem>
                        <SelectItem value="25">25</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-center space-x-2">
                    <p className="text-sm text-muted-foreground">
                      {page * rowsPerPage + 1}-
                      {Math.min(
                        (page + 1) * rowsPerPage,
                        filteredGrades.length,
                      )}{" "}
                      of {filteredGrades.length}
                    </p>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleChangePage(page - 1)}
                      disabled={page === 0}
                      className="transition-[transform,box-shadow] hover:scale-105 hover:shadow-md hover:border-primary hover:bg-primary/5 hover:text-primary disabled:hover:scale-100 disabled:hover:shadow-none disabled:hover:border-border disabled:hover:bg-transparent disabled:hover:text-muted-foreground"
                    >
                      {t("student.previous")}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleChangePage(page + 1)}
                      disabled={
                        (page + 1) * rowsPerPage >= filteredGrades.length
                      }
                      className="transition-[transform,box-shadow] hover:scale-105 hover:shadow-md hover:border-primary hover:bg-primary/5 hover:text-primary disabled:hover:scale-100 disabled:hover:shadow-none disabled:hover:border-border disabled:hover:bg-transparent disabled:hover:text-muted-foreground"
                    >
                      {t("student.next")}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </>
        ) : (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-16">
              <BookOpen className="h-16 w-16 text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold text-foreground mb-2">
                {t("student.noGradesAvailable")}
              </h3>
              <p className="text-muted-foreground text-center">
                {t("student.noGradesYet")}
              </p>
            </CardContent>
          </Card>
        )}
      </div>
    </TooltipProvider>
  );
};

export default StudentGrades;
